/**
 * F-GTCC-COCKPIT-EXEC1 — "Executive Summary" tab content: 5 KPI tiles, a
 * grid-main (Infrastructure host table + vehicle-flow/backup-readiness/jobs
 * trio + two charts) and a grid-bottom (alert-severity donut + synthetic +
 * delivery-watchdog + recent-changes panels), per
 * `GTCC_REFERENCE_2026-09-01.html` (`data-sec="exec"`).
 *
 * Read-only. Every fetcher comes from `../../exec-api.js`; no other data
 * source is queried from this tab. Never fabricates a number: a failed or
 * disabled fetch renders `ErrorState`/`EmptyState`, never a fallback value.
 */
import { Card, EmptyState, ErrorState, LoadingState, Stack } from "@genius-tracker/design-system";
import { useLocale } from "@genius-tracker/i18n/react";
import { useQuery, type UseQueryResult } from "@tanstack/react-query";
import type { CSSProperties, ReactNode } from "react";

import {
  FLEET_HOSTS_KEY,
  fetchFleetHosts,
  type FleetHostsDto,
  type FleetResult,
} from "../../api/fleet.js";
import {
  fetchGtccAlertSeverity,
  fetchGtccBackupReadiness,
  fetchGtccDeliveryWatchdog,
  fetchGtccExecSummary,
  fetchGtccJobs,
  fetchGtccLatencySeries,
  fetchGtccRecentChanges,
  fetchGtccSynthetic,
  fetchGtccTelemetryIngress,
  fetchGtccVehicleFlow,
  type GtccFetchResult,
} from "../../exec-api.js";
import type {
  GtccAlertSeverityEnvelopeDto,
  GtccExecSummaryDto,
  GtccLatencySeriesEnvelopeDto,
  GtccRecentChangesEnvelopeDto,
  GtccRowsEnvelopeDto,
  GtccRowVerdict,
  GtccTelemetryIngressEnvelopeDto,
} from "../../exec-types.js";
import {
  AlertSeverityDonut,
  LatencyChartPanel,
  TelemetryIngressChartPanel,
} from "./components/ExecCharts.js";
import { FreshBadge, RowsPanel, Tile } from "./components/ExecPanels.js";
import { HostTable } from "./components/HostTable.js";

/** Ticket floor: never refetch faster than 30 s. */
export const EXEC_TAB_STALE_MS = 30_000;
export const EXEC_TAB_REFETCH_MS = 60_000;

const GRID_MAIN_STYLE: CSSProperties = {
  display: "grid",
  gridTemplateColumns: "minmax(0, 2fr) minmax(0, 1fr)",
  gap: "var(--gt-sys-space-4, 16px)",
};

const GRID_BOTTOM_STYLE: CSSProperties = {
  display: "grid",
  gridTemplateColumns: "repeat(auto-fit, minmax(240px, 1fr))",
  gap: "var(--gt-sys-space-4, 16px)",
};

/**
 * A malformed (but 200 OK) response can send `null`, an object, or omit a
 * field entirely where the DTO type promises an array. That is the source's
 * error, not a reason to crash: the empty list is what "nicht messbar" looks
 * like for a table/chart panel (Owner P9/P10, see `DplanePanels.tsx`).
 */
function asList<T>(value: ReadonlyArray<T> | null | undefined): ReadonlyArray<T> {
  return Array.isArray(value) ? value : [];
}

const KNOWN_ROW_VERDICTS = ["ok", "warn", "crit", "info", "unmeasurable"] as const;

/**
 * A status field that is *present but garbled* (e.g. the wrong JSON type)
 * is a data-integrity problem, not an absent measurement — it must not be
 * silently treated as "unmeasurable", which would hide the corruption from
 * the operator (see `admin.gtcc.cockpit.exec.host_status.unavailable`).
 */
function isKnownRowVerdict(status: unknown): status is GtccRowVerdict {
  return typeof status === "string" && (KNOWN_ROW_VERDICTS as readonly string[]).includes(status);
}

function SectionCard(props: {
  title: string;
  freshBadge?: ReactNode;
  children: ReactNode;
}): ReactNode {
  return (
    <Card>
      <h3
        style={{
          margin: "0 0 var(--gt-sys-space-3, 12px)",
          display: "flex",
          alignItems: "baseline",
          gap: 8,
          fontSize: "var(--gt-sys-font-size-md)",
        }}
      >
        {props.title}
        {props.freshBadge}
      </h3>
      {props.children}
    </Card>
  );
}

function useGtccQuery<T>(
  key: string,
  fetcher: (signal?: AbortSignal) => Promise<GtccFetchResult<T>>,
) {
  return useQuery({
    queryKey: ["gtcc", "cockpit", key],
    queryFn: ({ signal }) => fetcher(signal),
    staleTime: EXEC_TAB_STALE_MS,
    refetchInterval: EXEC_TAB_REFETCH_MS,
  });
}

function Gate<T>(props: {
  query: UseQueryResult<GtccFetchResult<T>>;
  testId: string;
  render: (data: T) => ReactNode;
}): ReactNode {
  const { t } = useLocale();
  const { query } = props;
  if (query.isPending) return <LoadingState label={t("admin.gtcc.cockpit.loading")} />;
  if (query.isError) {
    return (
      <div data-testid={props.testId}>
        <ErrorState title={t("admin.gtcc.cockpit.error.title")} body={query.error.message} />
      </div>
    );
  }
  if (!query.data.ok) {
    if (query.data.kind === "forbidden") {
      return (
        <div data-testid={props.testId}>
          <ErrorState
            severity="warn"
            title={t("admin.gtcc.cockpit.forbidden.title")}
            body={t("admin.gtcc.cockpit.forbidden.body")}
          />
        </div>
      );
    }
    if (query.data.kind === "disabled") {
      return (
        <div data-testid={props.testId}>
          <EmptyState
            title={t("admin.gtcc.cockpit.disabled.title")}
            body={t("admin.gtcc.cockpit.disabled.body")}
          />
        </div>
      );
    }
    const body = query.data.kind === "transport_error" ? query.data.message : query.data.message;
    return (
      <div data-testid={props.testId}>
        <ErrorState title={t("admin.gtcc.cockpit.error.title")} body={body} />
      </div>
    );
  }
  return <>{props.render(query.data.data)}</>;
}

/**
 * `HostTable.tsx` is C-GTCC-COCKPIT-INFRA1's component and consumes
 * `FleetResult<T>` (kind `disabled`/`forbidden`/`upstream`/`transport_error`),
 * a different discriminated union than `Gate`'s `GtccFetchResult<T>`. This
 * mirrors `views/cockpit/tabs/InfraTab.tsx`'s `FleetGate` so both lanes read
 * the same failure copy for the same client.
 */
function FleetGate<T>(props: {
  query: UseQueryResult<FleetResult<T>>;
  testId: string;
  render: (data: T) => ReactNode;
}): ReactNode {
  const { t } = useLocale();
  const { query } = props;

  if (query.isPending) {
    return <LoadingState label={t("admin.gtcc.cockpit.loading")} />;
  }
  if (query.isError) {
    return (
      <div data-testid={`${props.testId}-failure`}>
        <ErrorState title={t("admin.gtcc.cockpit.error.title")} body={query.error.message} />
      </div>
    );
  }
  if (query.data.ok) return <>{props.render(query.data.data)}</>;

  const failure = query.data;
  const body =
    failure.kind === "disabled"
      ? t("admin.gtcc.cockpit.infra.failure.disabled")
      : failure.kind === "forbidden"
        ? t("admin.gtcc.cockpit.infra.failure.forbidden")
        : failure.kind === "upstream"
          ? t("admin.gtcc.cockpit.infra.failure.upstream", { status: failure.httpStatus })
          : t("admin.gtcc.cockpit.infra.failure.transport");
  return (
    <div data-testid={`${props.testId}-failure`}>
      <ErrorState severity="warn" title={t("admin.gtcc.cockpit.infra.failure.title")} body={body} />
    </div>
  );
}

export function ExecTab(): ReactNode {
  const { t } = useLocale();

  const summaryQuery = useGtccQuery<GtccExecSummaryDto>("exec-summary", fetchGtccExecSummary);
  const hostsQuery = useQuery<FleetResult<FleetHostsDto>>({
    queryKey: FLEET_HOSTS_KEY,
    queryFn: ({ signal }) => fetchFleetHosts(signal),
    staleTime: EXEC_TAB_STALE_MS,
    refetchInterval: EXEC_TAB_REFETCH_MS,
  });
  const latencyQuery = useGtccQuery<GtccLatencySeriesEnvelopeDto>(
    "latency-series",
    fetchGtccLatencySeries,
  );
  const telemetryQuery = useGtccQuery<GtccTelemetryIngressEnvelopeDto>(
    "telemetry-ingress",
    fetchGtccTelemetryIngress,
  );
  const severityQuery = useGtccQuery<GtccAlertSeverityEnvelopeDto>(
    "alert-severity",
    fetchGtccAlertSeverity,
  );
  const vehicleFlowQuery = useGtccQuery<GtccRowsEnvelopeDto>("vehicle-flow", fetchGtccVehicleFlow);
  const backupReadinessQuery = useGtccQuery<GtccRowsEnvelopeDto>(
    "backup-readiness",
    fetchGtccBackupReadiness,
  );
  const jobsQuery = useGtccQuery<GtccRowsEnvelopeDto>("jobs", fetchGtccJobs);
  const syntheticQuery = useGtccQuery<GtccRowsEnvelopeDto>("synthetic", fetchGtccSynthetic);
  const deliveryWatchdogQuery = useGtccQuery<GtccRowsEnvelopeDto>(
    "delivery-watchdog",
    fetchGtccDeliveryWatchdog,
  );
  const recentChangesQuery = useGtccQuery<GtccRecentChangesEnvelopeDto>(
    "recent-changes",
    fetchGtccRecentChanges,
  );

  return (
    <Stack gap={4} data-testid="gtcc-cockpit-exec-tab">
      <Gate
        query={summaryQuery}
        testId="gtcc-cockpit-exec-summary-gate"
        render={(summary) => (
          <div
            data-testid="gtcc-cockpit-exec-tiles"
            style={{
              display: "grid",
              gridTemplateColumns: "repeat(auto-fit, minmax(180px, 1fr))",
              gap: "var(--gt-sys-space-3, 12px)",
            }}
          >
            <Tile
              testId="gtcc-cockpit-exec-tile-platform-health"
              tone={
                isKnownRowVerdict(summary.platformHealth?.status)
                  ? summary.platformHealth.status
                  : "unmeasurable"
              }
              title={t("admin.gtcc.cockpit.exec.tile.platform_health")}
              freshBadge={<FreshBadge freshnessSeconds={summary.freshnessSeconds} />}
              big={
                summary.platformHealth?.status === undefined ||
                summary.platformHealth?.status === null
                  ? t("admin.gtcc.cockpit.exec.not_measurable")
                  : !isKnownRowVerdict(summary.platformHealth.status)
                    ? t("admin.gtcc.cockpit.exec.host_status.unavailable")
                    : summary.platformHealth.status === "unmeasurable"
                      ? t("admin.gtcc.cockpit.exec.not_measurable")
                      : t(
                          `admin.gtcc.cockpit.exec.host_status.${
                            summary.platformHealth.status === "info"
                              ? "ok"
                              : summary.platformHealth.status
                          }`,
                        )
              }
              status={summary.platformHealth?.note}
            />
            <Tile
              testId="gtcc-cockpit-exec-tile-active-alerts"
              tone={
                (summary.activeAlerts?.critical ?? 0) > 0
                  ? "crit"
                  : (summary.activeAlerts?.warning ?? 0) > 0
                    ? "warn"
                    : "ok"
              }
              title={t("admin.gtcc.cockpit.exec.tile.active_alerts")}
              big={summary.activeAlerts?.critical ?? 0}
              small={
                <small>
                  {t("admin.gtcc.cockpit.exec.tile.severity_small", {
                    warning: summary.activeAlerts?.warning ?? 0,
                  })}
                </small>
              }
              status={
                (summary.activeAlerts?.top ?? null) !== null
                  ? t("admin.gtcc.cockpit.exec.tile.top_alert", {
                      name: summary.activeAlerts?.top?.name ?? "",
                      minutes: Math.round((summary.activeAlerts?.top?.sinceSeconds ?? 0) / 60),
                    })
                  : t("admin.gtcc.cockpit.exec.tile.healthy")
              }
            />
            <Tile
              testId="gtcc-cockpit-exec-tile-active-incidents"
              tone={(summary.activeIncidents?.count ?? 0) > 0 ? "warn" : "ok"}
              title={t("admin.gtcc.cockpit.exec.tile.active_incidents")}
              big={summary.activeIncidents?.count ?? 0}
              status={
                (summary.activeIncidents?.count ?? 0) === 0
                  ? t("admin.gtcc.cockpit.exec.tile.no_incident_open")
                  : String(summary.activeIncidents?.count ?? 0)
              }
            />
            <Tile
              testId="gtcc-cockpit-exec-tile-data-confidence"
              tone={(summary.dataConfidence?.staleSources?.length ?? 0) > 0 ? "warn" : "ok"}
              title={t("admin.gtcc.cockpit.exec.tile.data_confidence")}
              freshBadge={
                (summary.dataConfidence?.staleSources?.length ?? 0) > 0 ? (
                  <FreshBadge
                    freshnessSeconds={summary.freshnessSeconds}
                    stale
                    label={t("admin.gtcc.cockpit.exec.tile.stale_sources_count", {
                      count: summary.dataConfidence?.staleSources?.length ?? 0,
                    })}
                  />
                ) : undefined
              }
              big={
                (summary.dataConfidence?.percent ?? null) !== null
                  ? `${summary.dataConfidence?.percent} %`
                  : t("admin.gtcc.cockpit.exec.not_measurable")
              }
              status={
                (summary.dataConfidence?.staleSources?.[0] ?? undefined) === undefined
                  ? t("admin.gtcc.cockpit.exec.tile.stale_sources_count", {
                      count: summary.dataConfidence?.staleSources?.length ?? 0,
                    })
                  : t("admin.gtcc.cockpit.exec.tile.stale_source_age", {
                      name: summary.dataConfidence?.staleSources?.[0]?.name ?? "",
                      minutes: Math.round(
                        (summary.dataConfidence?.staleSources?.[0]?.ageSeconds ?? 0) / 60,
                      ),
                    })
              }
            />
            <Tile
              testId="gtcc-cockpit-exec-tile-uptime-30d"
              tone="ok"
              title={t("admin.gtcc.cockpit.exec.tile.uptime_30d")}
              big={
                (summary.uptime30d?.percent ?? null) !== null
                  ? `${summary.uptime30d?.percent} %`
                  : t("admin.gtcc.cockpit.exec.not_measurable")
              }
              status={t("admin.gtcc.cockpit.exec.tile.slo_budget", {
                slo: summary.uptime30d?.sloPercent,
                remaining:
                  summary.uptime30d?.budgetRemainingPercent ??
                  t("admin.gtcc.cockpit.exec.not_measurable"),
              })}
            />
          </div>
        )}
      />

      <div style={GRID_MAIN_STYLE} data-testid="gtcc-cockpit-exec-grid-main">
        <Stack gap={4}>
          <SectionCard
            title={t("admin.gtcc.cockpit.exec.panel.hosts")}
            freshBadge={
              hostsQuery.data?.ok === true ? (
                <FreshBadge
                  freshnessSeconds={hostsQuery.data.data.freshnessSeconds}
                  stale={hostsQuery.data.data.status === "stale"}
                  testId="gtcc-cockpit-exec-hosts-fresh"
                />
              ) : undefined
            }
          >
            <FleetGate
              query={hostsQuery}
              testId="gtcc-cockpit-exec-hosts"
              render={(data) => (
                <HostTable hosts={asList(data.hosts)} testId="gtcc-cockpit-exec-host-table" />
              )}
            />
          </SectionCard>
          <div style={GRID_BOTTOM_STYLE}>
            <Card>
              <Gate
                query={vehicleFlowQuery}
                testId="gtcc-cockpit-exec-vehicle-flow-gate"
                render={(envelope) => (
                  <RowsPanel
                    title={t("admin.gtcc.cockpit.exec.panel.vehicle_flow")}
                    freshBadge={<FreshBadge freshnessSeconds={envelope.freshnessSeconds} />}
                    rows={asList(envelope.rows)}
                    emptyLabel={t("admin.gtcc.cockpit.exec.empty")}
                    testId="gtcc-cockpit-exec-vehicle-flow"
                  />
                )}
              />
            </Card>
            <Card>
              <Gate
                query={backupReadinessQuery}
                testId="gtcc-cockpit-exec-backup-readiness-gate"
                render={(envelope) => (
                  <RowsPanel
                    title={t("admin.gtcc.cockpit.exec.panel.backup_readiness")}
                    freshBadge={<FreshBadge freshnessSeconds={envelope.freshnessSeconds} />}
                    rows={asList(envelope.rows)}
                    emptyLabel={t("admin.gtcc.cockpit.exec.empty")}
                    testId="gtcc-cockpit-exec-backup-readiness"
                  />
                )}
              />
            </Card>
            <Card>
              <Gate
                query={jobsQuery}
                testId="gtcc-cockpit-exec-jobs-gate"
                render={(envelope) => (
                  <RowsPanel
                    title={t("admin.gtcc.cockpit.exec.panel.jobs")}
                    freshBadge={<FreshBadge freshnessSeconds={envelope.freshnessSeconds} />}
                    rows={asList(envelope.rows)}
                    emptyLabel={t("admin.gtcc.cockpit.exec.empty")}
                    testId="gtcc-cockpit-exec-jobs"
                  />
                )}
              />
            </Card>
          </div>
        </Stack>
        <Stack gap={4}>
          <SectionCard title={t("admin.gtcc.cockpit.exec.panel.latency_chart")}>
            <Gate
              query={latencyQuery}
              testId="gtcc-cockpit-exec-latency-gate"
              render={(envelope) => (
                <LatencyChartPanel
                  series={asList(envelope.series)}
                  legendLabels={{
                    api: t("admin.gtcc.cockpit.exec.legend.api"),
                    db: t("admin.gtcc.cockpit.exec.legend.db"),
                  }}
                  testId="gtcc-cockpit-exec-latency-chart"
                  ariaLabel={t("admin.gtcc.cockpit.exec.panel.latency_chart")}
                />
              )}
            />
          </SectionCard>
          <SectionCard title={t("admin.gtcc.cockpit.exec.panel.telemetry_chart")}>
            <Gate
              query={telemetryQuery}
              testId="gtcc-cockpit-exec-telemetry-gate"
              render={(envelope) => (
                <TelemetryIngressChartPanel
                  points={asList(envelope.points)}
                  annotations={asList(envelope.annotations)}
                  totalLabel={t("admin.gtcc.cockpit.exec.legend.telemetry_total")}
                  captionLabel={t("admin.gtcc.cockpit.exec.legend.telemetry_caption")}
                  testId="gtcc-cockpit-exec-telemetry-chart"
                  ariaLabel={t("admin.gtcc.cockpit.exec.panel.telemetry_chart")}
                />
              )}
            />
          </SectionCard>
        </Stack>
      </div>

      <div style={GRID_BOTTOM_STYLE} data-testid="gtcc-cockpit-exec-grid-bottom">
        <SectionCard title={t("admin.gtcc.cockpit.exec.panel.alert_severity")}>
          <Gate
            query={severityQuery}
            testId="gtcc-cockpit-exec-severity-gate"
            render={(envelope) => (
              <AlertSeverityDonut
                data={envelope}
                legendLabels={{
                  critical: t("admin.gtcc.cockpit.exec.legend.critical"),
                  high: t("admin.gtcc.cockpit.exec.legend.high"),
                  medium: t("admin.gtcc.cockpit.exec.legend.medium"),
                  low: t("admin.gtcc.cockpit.exec.legend.low"),
                }}
                testId="gtcc-cockpit-exec-severity-donut"
              />
            )}
          />
        </SectionCard>
        <Card>
          <Gate
            query={syntheticQuery}
            testId="gtcc-cockpit-exec-synthetic-gate"
            render={(envelope) => (
              <RowsPanel
                title={t("admin.gtcc.cockpit.exec.panel.synthetic")}
                freshBadge={<FreshBadge freshnessSeconds={envelope.freshnessSeconds} />}
                rows={asList(envelope.rows)}
                emptyLabel={t("admin.gtcc.cockpit.exec.empty")}
                testId="gtcc-cockpit-exec-synthetic"
              />
            )}
          />
        </Card>
        <Card>
          <Gate
            query={deliveryWatchdogQuery}
            testId="gtcc-cockpit-exec-delivery-watchdog-gate"
            render={(envelope) => (
              <RowsPanel
                title={t("admin.gtcc.cockpit.exec.panel.delivery_watchdog")}
                freshBadge={<FreshBadge freshnessSeconds={envelope.freshnessSeconds} />}
                rows={asList(envelope.rows)}
                emptyLabel={t("admin.gtcc.cockpit.exec.empty")}
                testId="gtcc-cockpit-exec-delivery-watchdog"
              />
            )}
          />
        </Card>
        <Card>
          <Gate
            query={recentChangesQuery}
            testId="gtcc-cockpit-exec-recent-changes-gate"
            render={(envelope) => (
              <RowsPanel
                title={t("admin.gtcc.cockpit.exec.panel.recent_changes")}
                freshBadge={<FreshBadge freshnessSeconds={envelope.freshnessSeconds} />}
                rows={asList(envelope.rows)}
                emptyLabel={t("admin.gtcc.cockpit.exec.empty")}
                testId="gtcc-cockpit-exec-recent-changes"
                {...(envelope.truncated
                  ? {
                      truncatedNote: t("admin.gtcc.cockpit.exec.panel.recent_changes_truncated", {
                        shown: envelope.rows.length,
                      }),
                    }
                  : {})}
              />
            )}
          />
        </Card>
      </div>
    </Stack>
  );
}
